import React, { useState } from 'react';
import type { User } from '../services/admin';
import {
  Card,
  Table,
  Tag,
  Space,
  Button,
  Row,
  Col,
  Statistic,
  Tooltip,
  Empty,
  message,
  Popconfirm,
  Modal,
  Select
} from 'antd';
import {
  SettingOutlined,
  UserOutlined,
  SafetyOutlined,
  DesktopOutlined,
  PlayCircleOutlined,
  CalendarOutlined,
  GlobalOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  StopOutlined,
  FilterOutlined,
  DeleteOutlined,
  PlusOutlined,
  EditOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminService } from '../services/admin';
import LicenseConfigModal from './LicenseConfigModal';
import dayjs from 'dayjs';

const { Option } = Select;

interface LicenseConfigOverviewProps {
  userId?: number; // Optional: if provided, shows only this user's config
}

const LicenseConfigOverview: React.FC<LicenseConfigOverviewProps> = ({ userId }) => {
  const queryClient = useQueryClient();
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [selectedConfig, setSelectedConfig] = useState<any>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [refreshingStatus, setRefreshingStatus] = useState<number | null>(null);
  const [userSelectionModalVisible, setUserSelectionModalVisible] = useState(false);

  // Parse JSON string data from backend
  const parseJsonData = (data: string, defaultValue: any[] = []) => {
    try {
      return JSON.parse(data);
    } catch (e) {
      return defaultValue;
    }
  };

  // Fetch all license configurations
  const { data, isLoading, refetch } = useQuery({
    queryKey: userId ? ['userLicenseConfig', userId] : ['allLicenseConfigs'],
    queryFn: userId
      ? () => adminService.getUserLicenseConfig(userId).then(config => config ? [config] : [])
      : () => adminService.getAllLicenseConfigs(),
  });

  // Transform data for display
  const transformedData = (data || []).map(config => ({
    ...config,
    // Parse JSON strings back to arrays/objects for display
    allowedContentTypes: parseJsonData(config.allowedContentTypes),
    restrictedContentTypes: parseJsonData(config.restrictedContentTypes),
    allowWeekdays: parseJsonData(config.allowWeekdays, [0,1,2,3,4,5,6]),
    allowedRegions: parseJsonData(config.allowedRegions),
    restrictedRegions: parseJsonData(config.restrictedRegions),
    allowedIPs: parseJsonData(config.allowedIPs),
    restrictedIPs: parseJsonData(config.restrictedIPs),
  }));

  const handleEdit = (record: any) => {
    setSelectedUserId(record.userId);
    setSelectedConfig(record);
    setModalVisible(true);
  };

  // Fetch all users - will be used for user selection in create mode
  const { data: allUsers = [] } = useQuery({
    queryKey: ['allUsers'],
    queryFn: adminService.getUsers,
  });

  // Get users who don't have license config
  const usersWithoutConfig = React.useMemo(() => {
    const userIdsWithConfig = new Set((data || []).map((config: any) => config.userId));
    return allUsers.filter((user: any) => !userIdsWithConfig.has(user.id));
  }, [allUsers, data]);

  const handleCreateConfig = async (data: any) => {
    if (!selectedUserId) return;

    try {
      await adminService.createLicenseConfig(selectedUserId, data);
      message.success('License configuration created successfully');
      refetch();
    } catch (error: any) {
      console.error('Create failed:', error);
      const errorMessage = error.response?.data?.message ||
                          error.response?.data?.error ||
                          'Failed to create license configuration';
      message.error(errorMessage);
      throw error;
    }
  };

  const handleUserSelect = (user: User) => {
    setSelectedUserId(user.id);
    // Create a default config object with isActive set to true for creation mode
    setSelectedConfig({
      maxDevices: 3,
      maxConcurrentPlays: 1,
      licenseDays: 7,
      maxPlayCount: 999999999,
      allowVideo: true,
      allowAudio: true,
      allowDocument: true,
      allowedContentTypes: ['all'],
      restrictedContentTypes: [],
      allowPlayStartTime: '00:00',
      allowPlayEndTime: '23:59',
      allowWeekdays: [0, 1, 2, 3, 4, 5, 6],
      allowedRegions: [],
      restrictedRegions: [],
      allowedIPs: [],
      restrictedIPs: [],
      isActive: true, // Ensure isActive is true by default
      notes: '',
      // Add user data for display purposes
      user: {
        id: user.id,
        username: user.username,
        email: user.email,
        status: user.status
      }
    });
    setUserSelectionModalVisible(false);
    setModalVisible(true);
  };

  const handleSaveConfig = async (data: any) => {
    if (!selectedUserId) return;

    try {
      await adminService.updateUserLicenseConfig(selectedUserId, data);
      message.success('License configuration updated successfully');
      refetch();
    } catch (error: any) {
      console.error('Save failed:', error);
      const errorMessage = error.response?.data?.message ||
                          error.response?.data?.error ||
                          'Failed to save license configuration';
      message.error(errorMessage);
      throw error; // Let modal handle the error display
    }
  };

  const handleToggleStatus = async (userId: number) => {
    setRefreshingStatus(userId);
    try {
      await adminService.toggleLicenseConfigStatus(userId);
      message.success('License configuration status updated');
      refetch();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to update status');
    } finally {
      setRefreshingStatus(null);
    }
  };

  // Delete configuration mutation
  const deleteConfigMutation = useMutation({
    mutationFn: adminService.deleteLicenseConfig,
    onSuccess: () => {
      message.success('License configuration deleted successfully');
      refetch();
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to delete license configuration');
    }
  });

  const handleDelete = async (userId: number) => {
    await deleteConfigMutation.mutateAsync(userId);
  };

  const getStatusTag = (isActive: boolean) => (
    <Tag color={isActive ? 'green' : 'red'}>
      {isActive ? 'Active' : 'Inactive'}
    </Tag>
  );

  const getContentTypeTags = (allowVideo: boolean, allowAudio: boolean, allowDocument: boolean) => (
    <Space size="small">
      <Tag color={allowVideo ? 'green' : 'red'}>
        <Space size={2}>
          <PlayCircleOutlined />
          Video
        </Space>
      </Tag>
      <Tag color={allowAudio ? 'green' : 'red'}>
        <Space size={2}>
          <DesktopOutlined />
          Audio
        </Space>
      </Tag>
      <Tag color={allowDocument ? 'green' : 'red'}>
        <Space size={2}>
          <CalendarOutlined />
          Documents
        </Space>
      </Tag>
    </Space>
  );

  const getTimeRestriction = (start: string, end: string) => {
    if (start === '00:00' && end === '23:59') {
      return <Tag color="green">24/7 Allowed</Tag>;
    }
    return <Tag color="blue">{start} - {end}</Tag>;
  };

  const columns = [
    {
      title: 'User',
      key: 'user',
      render: (_: any, record: any) => (
        <Space>
          <UserOutlined />
          <div>
            <div>{record.user.username}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>{record.user.email}</div>
          </div>
        </Space>
      ),
      sorter: (a: any, b: any) => a.user.username.localeCompare(b.user.username),
    },
    {
      title: 'User Status',
      key: 'userStatus',
      render: (_: any, record: any) => (
        <Tag color={record.user.status === 'active' ? 'green' : 'red'}>
          {record.user.status}
        </Tag>
      ),
      width: 100,
    },
    {
      title: 'Status',
      key: 'status',
      dataIndex: 'isActive',
      render: (isActive: boolean) => getStatusTag(isActive),
      width: 80,
    },
    {
      title: 'Devices',
      key: 'devices',
      className: 'text-center',
      render: (record: any) => (
        <div>
          <div>{record.maxDevices}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>max devices</div>
        </div>
      ),
      sorter: (a: any, b: any) => a.maxDevices - b.maxDevices,
      width: 90,
    },
    {
      title: 'Content Access',
      key: 'content',
      render: (_: any, record: any) => (
        <Tooltip title="Video/Audio/Document Content Types">
          {getContentTypeTags(record.allowVideo, record.allowAudio, record.allowDocument)}
        </Tooltip>
      ),
      width: 200,
    },
    {
      title: 'Time Restrictions',
      key: 'time',
      render: (_: any, record: any) => (
        <Tooltip title="Allowed play time windows">
          {getTimeRestriction(record.allowPlayStartTime, record.allowPlayEndTime)}
        </Tooltip>
      ),
      width: 120,
    },
    {
      title: 'Created',
      key: 'created',
      dataIndex: 'createdAt',
      render: (date: string) => (
        <Tooltip title={dayjs(date).format('YYYY-MM-DD HH:mm:ss')}>
          {dayjs(date).format('MMM DD, YY')}
        </Tooltip>
      ),
      defaultSortOrder: 'descend' as const,
      sorter: (a: any, b: any) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      width: 100,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: any) => (
        <Space size="middle">
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Button
            type="text"
            icon={record.isActive ? <StopOutlined /> : <CheckCircleOutlined />}
            onClick={() => handleToggleStatus(record.userId)}
            loading={refreshingStatus === record.userId}
          >
            {record.isActive ? 'Disable' : 'Enable'}
          </Button>
          <Popconfirm
            title="Delete License Configuration"
            description="Are you sure you want to delete this license configuration? This action cannot be undone."
            onConfirm={() => handleDelete(record.userId)}
            okText="Yes, Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              loading={deleteConfigMutation.isPending && deleteConfigMutation.variables === record.userId}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
      width: 150,
    },
  ];

  if (userId) {
    // Show individual user config without delete button
    const filteredColumns = columns.filter(col =>
      !['user', 'userStatus'].includes(col.key!.toString())
    );

    return (
      <div>
        <Card title="License Configuration" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={6}>
              <Statistic
                title="Max Devices"
                value={transformedData[0]?.maxDevices || 'N/A'}
                prefix={<DesktopOutlined />}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Concurrent Plays"
                value={transformedData[0]?.maxConcurrentPlays || 'N/A'}
                prefix={<PlayCircleOutlined />}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="License Duration"
                value={transformedData[0]?.licenseDays + ' days' || 'N/A'}
                prefix={<CalendarOutlined />}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Status"
                value={transformedData[0]?.isActive ? 'Active' : 'Inactive'}
                prefix={transformedData[0]?.isActive ? <CheckCircleOutlined /> : <StopOutlined />}
              />
            </Col>
          </Row>
        </Card>
        <LicenseConfigModal
          visible={modalVisible}
          userId={userId}
          config={selectedConfig}
          onSave={handleSaveConfig}
          onCancel={() => setModalVisible(false)}
          currentConfig={selectedConfig}
        />
        <div style={{ marginTop: 16, textAlign: 'center' }}>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => handleEdit(transformedData[0])}
          >
            Edit Configuration
          </Button>
        </div>
      </div>
    );
  }

  return (
    <Card title="License Configuration Overview" style={{ margin: '16px 0' }}>
      <div style={{ marginBottom: 16, textAlign: 'right' }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            if (usersWithoutConfig.length === 0) {
              message.warning('All users already have license configurations');
              return;
            }
            setUserSelectionModalVisible(true);
          }}
        >
          Create New Configuration
        </Button>
      </div>

      <Table
        dataSource={transformedData}
        columns={columns}
        rowKey="id"
        loading={isLoading}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `Total ${total} configurations`,
        }}
        scroll={{ x: 800 }}
        locale={{ emptyText: <Empty description={<p>No license configurations found</p>} /> }}
      />

      {/* User Selection Modal */}
      <Modal
        title="Select User for License Configuration"
        open={userSelectionModalVisible}
        onCancel={() => setUserSelectionModalVisible(false)}
        footer={null}
        width={600}
      >
        {usersWithoutConfig.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Empty description="No users without license configurations" />
          </div>
        ) : (
          <Table
            dataSource={usersWithoutConfig}
            columns={[
              {
                title: 'User',
                key: 'user',
                render: (user: any) => (
                  <Space>
                    <UserOutlined />
                    <div>
                      <div>{user.username}</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>{user.email}</div>
                    </div>
                  </Space>
                )
              },
              {
                title: 'Role',
                dataIndex: 'role',
                key: 'role',
                render: (role: string) => (
                  <Tag style={{ textTransform: 'capitalize' }}>{role}</Tag>
                )
              },
              {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                render: (status: string) => (
                  <Tag color={status === 'active' ? 'green' : 'red'}>
                    {status}
                  </Tag>
                )
              },
              {
                title: 'Action',
                key: 'action',
                render: (user: any) => (
                  <Button
                    type="primary"
                    size="small"
                    onClick={() => handleUserSelect(user)}
                  >
                    Select
                  </Button>
                ),
                width: 100
              }
            ]}
            rowKey="id"
            size="small"
            pagination={{
              pageSize: 10,
              showSizeChanger: false
            }}
          />
        )}
      </Modal>

      <LicenseConfigModal
        visible={modalVisible}
        userId={selectedUserId!}
        config={selectedConfig}
        onSave={selectedConfig && selectedConfig.id ? handleSaveConfig : handleCreateConfig}
        onCancel={() => setModalVisible(false)}
        currentConfig={selectedConfig}
      />
    </Card>
  );
};

export default LicenseConfigOverview;